import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
// Imported for side effects: installs window error/rejection listeners before hydration.
import { captureClientError, initSentryClient } from "./lib/sentry-client";

if (typeof window !== "undefined") {
  void initSentryClient();
}

function describeKey(key: unknown) {
  try {
    return JSON.stringify(key)?.slice(0, 200);
  } catch {
    return undefined;
  }
}

export const getRouter = () => {
  // Per-request client (SSR safety). Global caches report every failed read
  // and write, so no query or mutation error can fail silently.
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        void captureClientError(error, {
          surface: "react_query",
          kind: "query",
          queryKey: describeKey(query.queryKey),
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        void captureClientError(error, {
          surface: "react_query",
          kind: "mutation",
          mutationKey: describeKey(mutation.options.mutationKey),
        });
      },
    }),
  });

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
  });

  return router;
};
